import { useDairySummary } from '@/api/hooks';
import { DairySubnav } from '@/components/dairy/DairySubnav';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { formatLiters } from '@/lib/dairy';
import { formatDate } from '@/lib/format';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';

export function DairyDashboardPage() {
  const { propertyId } = usePropertyScope();
  const { data, isLoading } = useDairySummary(propertyId);

  const maxSeries = useMemo(
    () => Math.max(1, ...(data?.daily_series.map((d) => d.total_liters) ?? [1])),
    [data?.daily_series],
  );

  return (
    <div>
      <PageHeader
        title="Controle Leiteiro"
        subtitle="Produção, indicadores e histórico de ordenhas"
        actions={
          <Link
            to="/controle-leiteiro/lancamentos"
            className="inline-flex rounded-xl bg-manejai-forest px-3 py-2 text-sm font-medium text-white hover:bg-manejai-moss"
          >
            Novo lançamento
          </Link>
        }
      />
      <DairySubnav />

      {!propertyId ? (
        <Card>
          <CardBody className="py-12 text-center text-manejai-bark/60">
            Selecione uma propriedade para ver o controle leiteiro.
          </CardBody>
        </Card>
      ) : isLoading ? (
        <div className="grid gap-4 sm:grid-cols-3 mb-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-manejai-sand/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3 mb-6">
            <Metric label="Produção hoje" value={formatLiters(data?.production_today)} />
            <Metric label="Produção do mês" value={formatLiters(data?.production_month)} />
            <Metric label="Média por vaca/dia" value={formatLiters(data?.avg_per_cow_day)} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2 mb-6">
            <Card>
              <CardHeader>
                <h2 className="font-display font-semibold text-manejai-forest">Produção por lote</h2>
              </CardHeader>
              <CardBody className="p-0">
                {(data?.by_lot.length ?? 0) === 0 ? (
                  <p className="px-5 py-8 text-sm text-center text-manejai-bark/55">Sem dados no mês.</p>
                ) : (
                  <ul className="divide-y divide-manejai-sand/60">
                    {data!.by_lot.map((row) => (
                      <li key={row.lot_id ?? 'none'} className="flex justify-between px-5 py-3 text-sm">
                        <span className="text-manejai-forest">{row.lot_name}</span>
                        <span className="font-medium">{formatLiters(row.total_liters)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="font-display font-semibold text-manejai-forest">Top 10 vacas</h2>
              </CardHeader>
              <CardBody className="p-0">
                {(data?.top_cows.length ?? 0) === 0 ? (
                  <p className="px-5 py-8 text-sm text-center text-manejai-bark/55">Sem dados no mês.</p>
                ) : (
                  <ul className="divide-y divide-manejai-sand/60">
                    {data!.top_cows.map((row) => (
                      <li key={row.cow_id} className="flex justify-between gap-3 px-5 py-3 text-sm">
                        <Link to={`/animais/${row.cow_id}`} className="text-manejai-moss hover:underline truncate">
                          {row.internal_number}
                          {row.name ? ` — ${row.name}` : ''}
                        </Link>
                        <span className="font-medium shrink-0">{formatLiters(row.total_liters)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <h2 className="font-display font-semibold text-manejai-forest">Evolução (30 dias)</h2>
            </CardHeader>
            <CardBody>
              {(data?.daily_series.length ?? 0) === 0 ? (
                <p className="text-sm text-manejai-bark/55 text-center py-6">Sem produção no período.</p>
              ) : (
                <div className="flex items-end gap-1 h-40">
                  {data!.daily_series.map((d) => (
                    <div key={d.date} className="flex-1 min-w-0 flex flex-col items-center gap-1 h-full justify-end">
                      <div
                        className="w-full rounded-t bg-manejai-moss/80"
                        style={{ height: `${Math.max(4, (d.total_liters / maxSeries) * 100)}%` }}
                        title={`${formatDate(d.date)}: ${formatLiters(d.total_liters)}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardBody>
        <p className="text-xs text-manejai-bark/60">{label}</p>
        <p className="font-display text-2xl font-semibold text-manejai-forest mt-1">{value}</p>
      </CardBody>
    </Card>
  );
}
